<template>
  <div>
    <b-sidebar
      id="circular-form-sidebar"
      ref="mySidebar"
      bg-variant="white"
      v-model="visibility"
      class="circular-sidebar"
      width="520px"
      right
      no-header
      backdrop
      shadow
    >
      <div class="sidebar-header-modern">
        <div class="sidebar-header-content">
          <div class="sidebar-title-wrapper">
            <feather-icon
              :icon="myObj.id > 0 ? 'EditIcon' : 'PlusIcon'"
              size="20"
              class="sidebar-title-icon"
            />
            <div>
              <h5 class="mb-0 sidebar-title">{{ sidebarTitle }}</h5>
              <small class="sidebar-subtitle d-block">{{
                sidebarSubtitle
              }}</small>
            </div>
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            class="sidebar-close-btn"
            @click="visibility = false"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
      </div>

      <div class="sidebar-body-modern">
        <b-form @submit.prevent class="sidebar-form-modern">
          <b-row>
            <b-col cols="12" v-if="shouldShowCampusDropdown">
              <b-form-group
                label="Campus"
                invalid-feedback="Please select a campus"
                ref="campus"
              >
                <v-select
                  v-model="selectedCampuses"
                  :options="campuses"
                  :reduce="(val) => val.id"
                  label="branch"
                  multiple
                  :closeOnSelect="false"
                  :clearable="false"
                  placeholder="Select campus"
                  @input="onCampusChange"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group
                label="Recipient Type"
                invalid-feedback="Please select recipient type"
                ref="recipientType"
              >
                <v-select
                  v-model="myObj.circularFor"
                  :options="recipientTypes"
                  :reduce="(val) => val.value"
                  label="label"
                  :clearable="false"
                  placeholder="Select recipient type"
                  @input="onRecipientTypeChange"
                />
              </b-form-group>
            </b-col>

            <!-- Staff Toggle and Selection -->
            <b-col
              cols="12"
              v-if="
                myObj.circularFor === 'staff' && !isMultipleCampusesSelected
              "
            >
              <b-form-group label="Select Staff">
                <b-form-checkbox
                  v-model="selectSpecificStaff"
                  switch
                  class="custom-toggle"
                >
                  Select Specific Staff
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <!-- Staff Search Field -->
            <b-col
              cols="12"
              v-if="
                myObj.circularFor === 'staff' &&
                selectSpecificStaff &&
                !isMultipleCampusesSelected
              "
            >
              <b-form-group
                label="Select Staff"
                invalid-feedback="Please select at least one staff member"
                ref="staffSelection"
              >
                <v-select
                  v-model="selectedStaffMembers"
                  :options="staffOptions"
                  :reduce="(val) => val"
                  label="displayName"
                  multiple
                  :clearable="false"
                  placeholder="Search and select staff members"
                  @search="searchStaff"
                  @input="onStaffSelectionChange"
                  :loading="staffLoading"
                  :filterable="false"
                >
                  <template #option="{ name, designation, department }">
                    <div class="staff-option-item">
                      <div class="staff-main-info">
                        <div class="staff-name-section">
                          <strong class="staff-name">{{ name }}</strong>
                          <span class="staff-icon">
                            <feather-icon
                              icon="UserIcon"
                              size="12"
                              class="detail-icon"
                            />
                          </span>
                        </div>
                        <div class="staff-details-compact">
                          <span
                            v-if="designation"
                            class="detail-item-compact staff-designation"
                          >
                            <feather-icon
                              icon="BriefcaseIcon"
                              size="10"
                              class="detail-icon"
                            />
                            <strong>{{ designation }}</strong>
                          </span>
                          <span v-if="department" class="detail-item-compact">
                            <feather-icon
                              icon="BuildingIcon"
                              size="10"
                              class="detail-icon"
                            />
                            <strong>{{ department }}</strong>
                          </span>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template
                    #selected-option="{ name, designation, department }"
                  >
                    <div class="d-flex flex-column">
                      <strong>{{ name }}</strong>
                      <small class="text-muted">
                        <span v-if="designation">{{ designation }}</span>
                        <span v-if="designation && department"> • </span>
                        <span v-if="department">{{ department }}</span>
                      </small>
                    </div>
                  </template>
                </v-select>

                <!-- Show selected staff below the search -->
                <div
                  v-if="selectedStaffMembers && selectedStaffMembers.length > 0"
                  class="mt-3"
                >
                  <div class="selected-staff-header">
                    <h6 class="selected-count mb-0">
                      <feather-icon icon="UsersIcon" size="16" class="mr-2" />
                      Selected Staff ({{ selectedStaffMembers.length }})
                    </h6>
                  </div>
                  <div class="selected-staff-grid">
                    <div
                      v-for="(staff, index) in selectedStaffMembers"
                      :key="staff.id"
                      class="selected-staff-card"
                    >
                      <div class="staff-card-content">
                        <div class="staff-card-header">
                          <div class="staff-avatar">
                            <feather-icon icon="UserIcon" size="16" />
                          </div>
                          <div class="staff-card-info">
                            <div class="staff-card-name">{{ staff.name }}</div>
                            <div
                              v-if="staff.designation"
                              class="staff-card-designation"
                            >
                              {{ staff.designation }}
                            </div>
                          </div>
                          <button
                            @click="removeStaff(index)"
                            class="remove-staff-btn"
                            title="Remove staff member"
                          >
                            <feather-icon icon="XIcon" size="14" />
                          </button>
                        </div>
                        <div class="staff-card-details">
                          <span v-if="staff.department" class="detail-chip">
                            <feather-icon icon="BuildingIcon" size="12" />
                            <span>{{ staff.department }}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-form-group>
            </b-col>

            <!-- Department Dropdown for Staff -->
            <b-col
              cols="12"
              v-if="myObj.circularFor === 'staff' && !selectSpecificStaff"
            >
              <b-form-group
                label="Department"
                invalid-feedback="Please select a department"
                ref="department"
              >
                <v-select
                  v-model="myObj.depID"
                  :options="departments"
                  :reduce="(val) => val.id"
                  label="name"
                  multiple
                  :closeOnSelect="false"
                  :clearable="false"
                  placeholder="Select department"
                  @input="onDepartmentChange"
                />
              </b-form-group>
            </b-col>

            <!-- Student Toggle and Selection -->
            <b-col
              cols="12"
              v-if="
                myObj.circularFor === 'student' && !isMultipleCampusesSelected
              "
            >
              <b-form-group label="Select Students">
                <b-form-checkbox
                  v-model="selectSpecificStudents"
                  switch
                  class="custom-toggle"
                >
                  Select Specific Students
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <!-- Student Search Field -->
            <b-col
              cols="12"
              v-if="
                myObj.circularFor === 'student' &&
                selectSpecificStudents &&
                !isMultipleCampusesSelected
              "
            >
              <b-form-group
                label="Select Students"
                invalid-feedback="Please select at least one student"
                ref="studentSelection"
              >
                <v-select
                  v-model="selectedStudentMembers"
                  :options="studentOptions"
                  :reduce="(val) => val"
                  label="displayName"
                  multiple
                  :clearable="false"
                  placeholder="Search and select students"
                  @search="searchStudents"
                  @input="onStudentSelectionChange"
                  :loading="studentLoading"
                  :filterable="false"
                  class="enhanced-student-select"
                >
                  <template
                    #option="{
                      name,
                      mother_name,
                      roll_number,
                      className,
                      sectionName,
                      admission_number,
                      phone,
                      address,
                      gender,
                      grNo,
                    }"
                  >
                    <div class="student-option-item">
                      <div class="student-main-info">
                        <div class="student-name-section">
                          <strong class="student-name">{{ name }}</strong>
                          <span
                            v-if="gender"
                            class="student-gender"
                            :class="gender.toLowerCase()"
                          >
                            <feather-icon
                              :icon="
                                gender.toLowerCase() === 'male'
                                  ? 'UserIcon'
                                  : 'UserIcon'
                              "
                              size="12"
                              class="detail-icon"
                            />
                          </span>
                        </div>
                        <div class="student-details-compact">
                          <span
                            v-if="grNo"
                            class="detail-item-compact gr-number"
                          >
                            <feather-icon
                              icon="HashIcon"
                              size="10"
                              class="detail-icon"
                            />
                            <strong>GR:</strong> {{ grNo }}
                          </span>
                          <span v-if="className" class="detail-item-compact">
                            <feather-icon
                              icon="BookOpenIcon"
                              size="10"
                              class="detail-icon"
                            />
                            <strong
                              >{{ className
                              }}{{
                                sectionName ? " - " + sectionName : ""
                              }}</strong
                            >
                          </span>
                          <span v-if="roll_number" class="detail-item-compact">
                            <feather-icon
                              icon="HashIcon"
                              size="10"
                              class="detail-icon"
                            />
                            <strong>Roll:</strong> {{ roll_number }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </template>
                </v-select>

                <!-- Show selected students below the search -->
                <div
                  v-if="
                    selectedStudentMembers && selectedStudentMembers.length > 0
                  "
                  class="mt-3"
                >
                  <div class="selected-students-header">
                    <feather-icon icon="UsersIcon" size="16" class="mr-1" />
                    <span class="selected-count"
                      >{{ selectedStudentMembers.length }} Student{{
                        selectedStudentMembers.length > 1 ? "s" : ""
                      }}
                      Selected</span
                    >
                  </div>
                  <div class="selected-students-grid">
                    <div
                      v-for="(student, index) in selectedStudentMembers"
                      :key="student.id"
                      class="selected-student-card"
                    >
                      <div class="student-card-content">
                        <div class="student-card-header">
                          <div class="student-avatar">
                            <feather-icon icon="UserIcon" size="20" />
                          </div>
                          <div class="student-card-info">
                            <strong class="student-card-name">{{
                              student.name
                            }}</strong>
                            <small v-if="student.grNo" class="student-card-gr"
                              >GR: {{ student.grNo }}</small
                            >
                          </div>
                          <button
                            class="remove-student-btn"
                            @click="removeStudent(index)"
                            title="Remove student"
                          >
                            <feather-icon icon="XIcon" size="14" />
                          </button>
                        </div>
                        <div class="student-card-details">
                          <div class="detail-chip" v-if="student.className">
                            <feather-icon icon="BookOpenIcon" size="12" />
                            <span
                              >{{ student.className
                              }}{{
                                student.sectionName
                                  ? " - " + student.sectionName
                                  : ""
                              }}</span
                            >
                          </div>
                          <div class="detail-chip" v-if="student.roll_number">
                            <feather-icon icon="HashIcon" size="12" />
                            <span>Roll: {{ student.roll_number }}</span>
                          </div>
                          <div
                            class="detail-chip"
                            v-if="student.admission_number"
                          >
                            <feather-icon icon="FileTextIcon" size="12" />
                            <span>Adm: {{ student.admission_number }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-form-group>
            </b-col>

            <!-- Class and Section for Students (when toggle is off) -->
            <b-col
              cols="12"
              v-if="myObj.circularFor === 'student' && !selectSpecificStudents"
            >
              <b-form-group
                label="Class"
                invalid-feedback="Please select a class"
                ref="class"
              >
                <v-select
                  v-model="myObj.classIDs"
                  :options="classOptions"
                  :reduce="(val) => val.id"
                  label="name"
                  multiple
                  :closeOnSelect="false"
                  :clearable="false"
                  placeholder="Select class"
                  :disabled="loadingClasses || selectedCampuses.length === 0"
                  @input="onClassChange"
                />
              </b-form-group>
            </b-col>

            <b-col
              cols="12"
              v-if="myObj.circularFor === 'student' && !selectSpecificStudents"
            >
              <b-form-group
                label="Section"
                invalid-feedback="Please select a section"
                ref="section"
              >
                <v-select
                  v-model="myObj.secIDs"
                  :options="sections"
                  :reduce="(val) => val.id"
                  label="section"
                  multiple
                  :closeOnSelect="false"
                  :clearable="false"
                  placeholder="Select section"
                  :disabled="loadingClasses || !hasStudentClassSelected"
                  @input="onSectionChange"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group
                label="Title"
                invalid-feedback="Title is required"
                ref="title"
              >
                <b-form-input
                  v-model.trim="myObj.title"
                  placeholder="Enter circular title"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group
                label="Date Range"
                invalid-feedback="Date range is required"
                ref="dateRange"
              >
                <flat-pickr
                  v-model="dateRange"
                  :config="dateConfig"
                  class="form-control"
                  placeholder="Select date range"
                  @on-change="onDateRangeChange"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group
                label="Description"
                invalid-feedback="Description is required"
                ref="description"
              >
                <b-form-textarea
                  v-model.trim="myObj.description"
                  rows="3"
                  placeholder="Enter circular description"
                />
              </b-form-group>
            </b-col>

            <b-col md="12" class="modern-attachment-zone">
              <div class="d-flex justify-content-between">
                <label class="bv-no-focus-ring col-form-label pt-0"
                  >Attachments</label
                >
              </div>
              <div class="d-flex flex-wrap">
                <div
                  class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1"
                  style="width: 80px; height: 80px"
                  @click="!docLoading && $refs.noticeDocs.click()"
                >
                  <b-spinner
                    v-if="docLoading"
                    small
                    label="Loading..."
                    variant="primary"
                  />
                  <feather-icon
                    v-else
                    icon="PlusIcon"
                    size="34"
                    class="text-primary"
                  />
                </div>
                <div
                  class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1"
                  style="width: 80px; height: 80px; position: relative"
                  v-for="(file, index) in docList"
                  :key="index"
                  @click="openFile(getDocUrl(file))"
                >
                  <b-avatar
                    size="20"
                    variant="primary"
                    style="position: absolute; top: -5px; right: -5px"
                    button
                    @click.stop="removeDoc(index)"
                  >
                    <feather-icon icon="XIcon" />
                  </b-avatar>
                  <template v-if="isPreviewableDoc(file)">
                    <b-img
                      :src="getDocPreviewUrl(file)"
                      referrerpolicy="no-referrer"
                      class="w-100 h-100 rounded"
                      style="object-fit: cover"
                      @error="onDocPreviewError($event, file)"
                    />
                  </template>
                  <template v-else>
                    <feather-icon
                      icon="FileTextIcon"
                      size="30"
                      class="text-primary"
                    />
                  </template>
                </div>

                <input
                  type="file"
                  id="noticeDocs"
                  hidden
                  ref="noticeDocs"
                  multiple
                  @change="addDoc()"
                  accept="image/*,application/pdf,.doc,.docx,.txt"
                />
              </div>
            </b-col>
          </b-row>

          <div class="sidebar-footer-modern">
            <b-button
              variant="gradient-primary"
              block
              class="modern-btn-submit"
              :disabled="submitting"
              @click="handleSubmit"
            >
              <b-spinner v-if="submitting" small type="grow" class="mr-50" />
              <template v-else>
                <feather-icon icon="SaveIcon" size="16" class="mr-50" />
                <span>{{ myObj.id > 0 ? "Update" : "Save" }}</span>
              </template>
            </b-button>
          </div>
        </b-form>
      </div>
    </b-sidebar>

    <!-- View Details Modal -->
    <b-modal
      v-model="showViewModal"
      hide-header
      @hidden="selectedItem = null"
      centered
      no-close-on-backdrop
      size="lg"
      body-class="p-0"
      content-class="lr-modal-content circular-view-modal"
    >
      <div class="lr-modal-header">
        <div class="lr-modal-header-left">
          <div class="lr-modal-icon-wrap">
            <feather-icon icon="BellIcon" size="20" />
          </div>
          <div>
            <h5 class="lr-modal-title mb-0">Circular Details</h5>
            <small class="lr-modal-subtitle">School circular record</small>
          </div>
        </div>
        <b-button
          variant="flat-secondary"
          class="lr-modal-close-btn"
          @click="showViewModal = false"
        >
          <feather-icon icon="XIcon" size="18" />
        </b-button>
      </div>

      <div v-if="selectedItem" class="lr-modal-body">
        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="FileTextIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">Circular Information</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">Title</span>
              <span class="details-value details-value-primary">{{
                selectedItem.title || "N/A"
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">Recipient Type</span>
              <span class="details-value">
                <b-badge
                  :variant="
                    selectedItem.circularFor === 'all'
                      ? 'light-primary'
                      : selectedItem.circularFor === 'staff'
                      ? 'light-info'
                      : 'light-success'
                  "
                  class="details-badge text-capitalize"
                >
                  {{ selectedItem.circularFor }}
                </b-badge>
              </span>
            </div>
            <div
              v-if="getRecipientDisplay(selectedItem)"
              class="details-meta-item"
            >
              <span class="details-label">Recipients</span>
              <span class="details-value details-value-info">{{
                getRecipientDisplay(selectedItem)
              }}</span>
            </div>
          </div>
        </div>

        <div class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="CalendarIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">Schedule</h6>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">From Date</span>
              <span class="details-value details-value-primary">{{
                selectedItem.dtFrom
                  ? new Date(selectedItem.dtFrom).toLocaleDateString()
                  : "N/A"
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">To Date</span>
              <span class="details-value details-value-primary">{{
                selectedItem.dtTo
                  ? new Date(selectedItem.dtTo).toLocaleDateString()
                  : "N/A"
              }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="selectedItem.metaCampusName"
          class="leave-details-section mb-3"
        >
          <div class="section-header">
            <feather-icon icon="MapPinIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">
              Campuses ({{ getCampusList(selectedItem.metaCampusName).length }})
            </h6>
          </div>
          <div class="details-campus-grid">
            <span
              v-for="(campus, index) in getCampusList(
                selectedItem.metaCampusName
              )"
              :key="index"
              class="details-campus-chip"
            >
              <feather-icon icon="BuildingIcon" size="14" />
              {{ campus }}
            </span>
          </div>
        </div>

        <div v-if="selectedItem.description" class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="AlignLeftIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">Description</h6>
          </div>
          <div class="details-note-item">
            <div class="details-note-content">
              {{ selectedItem.description }}
            </div>
          </div>
        </div>

        <div v-if="selectedItem.attachment" class="leave-details-section mb-3">
          <div class="section-header">
            <feather-icon icon="PaperclipIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">Attachments</h6>
          </div>
          <div class="details-attachment-grid">
            <div
              v-for="(file, index) in selectedItem.attachment.split(',')"
              :key="index"
              class="details-attachment-card"
              @click="openFile(file)"
            >
              <feather-icon icon="FileTextIcon" size="16" />
              <span>Attachment {{ index + 1 }}</span>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showViewModal = false"
          class="lr-modal-footer-btn"
        >
          <feather-icon icon="XIcon" size="14" class="mr-50" />
          Close
        </b-button>
      </template>
    </b-modal>

    <b-card class="planner-list-card circulars-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">Circulars</h5>
          <small class="text-muted"
            >Manage school circulars for students and staff</small
          >
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPerPageChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="onPageChange"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col
          cols="12"
          class="filter-toolbar circulars-filter-toolbar d-flex align-items-center"
        >
          <div
            class="filter-fields-group filter-fields-group--cols-5 d-flex align-items-center"
          >
            <b-form-input
              v-model.trim="searchQuery"
              class="filter-search-input"
              placeholder="Search circulars..."
            />
            <v-select
              v-model="listFilters.circularFor"
              :options="recipientTypes"
              label="label"
              :reduce="(val) => val.value"
              :clearable="true"
              placeholder="Recipient"
              append-to-body
              class="filter-control filter-field-item"
              @input="onListFilterChange"
            />
            <v-select
              v-model="listFilters.classID"
              :options="classOptions"
              label="name"
              :reduce="(val) => val.id"
              :clearable="true"
              placeholder="Class"
              :disabled="loadingClasses"
              append-to-body
              class="filter-control filter-field-item"
              @input="onListFilterChange"
            />
            <div class="filter-date-wrap filter-field-item">
              <flat-pickr
                v-model="listDateRange"
                :config="listDateConfig"
                class="filter-date-item"
                placeholder="Date range"
                @on-change="onListDateRangeChange"
              />
            </div>
            <b-button
              variant="outline-secondary"
              class="action-btn-outline filter-clear-btn"
              @click="clearListFilters"
              v-b-tooltip.hover.top
              title="Clear filters"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openSidebar()"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Circular</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container circulars-table-scroll">
        <b-table
          class="planner-list-table circulars-list-table mt-1"
          :items="filteredItems"
          :fields="fields"
          :busy="loading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 class="mt-1 mb-1 text-center">{{ $t("No records found") }}</h3>
          </template>

          <template #table-busy>
            <tbody>
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + index"
                  class="skeleton-cell"
                >
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(title)="data">
            <span class="table-title-text" :title="data.value">{{
              data.value
            }}</span>
          </template>

          <template #cell(circularFor)="data">
            <b-badge
              :variant="
                data.value === 'all'
                  ? 'light-primary'
                  : data.value === 'staff'
                  ? 'light-info'
                  : 'light-success'
              "
              class="text-capitalize"
            >
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(description)="data">
            <span class="table-cell-text" :title="data.value">
              {{ data.value || "—" }}
            </span>
          </template>

          <template #cell(campuses)="data">
            <span
              class="table-cell-text"
              :title="data.item.metaCampusName || 'No campuses'"
            >
              {{ getTruncatedCampuses(data.item.metaCampusName) }}
            </span>
          </template>

          <template #cell(metaData)="data">
            <span
              class="table-cell-text"
              :title="getRecipientDisplay(data.item)"
            >
              {{ getTruncatedRecipients(data.item) }}
            </span>
          </template>

          <template #cell(dtFrom)="data">
            <span class="table-date-text">{{ data.value }}</span>
          </template>

          <template #cell(dtTo)="data">
            <span class="table-date-text">{{ data.value }}</span>
          </template>

          <template #head(actions)>
            <div class="text-center">
              <span>Actions</span>
            </div>
          </template>

          <template #cell(actions)="data">
            <div
              class="circulars-actions-cell d-flex justify-content-center flex-nowrap"
            >
              <b-button
                variant="outline-primary"
                v-b-tooltip.hover.top
                title="View details"
                class="action-btn-outline mr-1"
                @click="viewItem(data.item)"
              >
                <feather-icon icon="EyeIcon" />
              </b-button>
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="Edit"
                class="action-btn-table mr-1"
                @click="editItem(data.item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                class="action-btn-outline delete-btn"
                @click="deleteItem(data.item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BAvatar,
  BSpinner,
  BFormGroup,
  BForm,
  BSidebar,
  BFormTextarea,
  BImg,
  BFormCheckbox,
  BPagination,
  BBadge,
  BModal,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import {
  buildUploadedAttachment,
  getDocPreviewUrl as resolveDocPreviewUrl,
  getDocUrl as resolveDocUrl,
  handleDrivePreviewError,
  isPreviewableDoc as checkPreviewableDoc,
  parseDocUrls,
  serializeDocUrls,
  uploadToMySkool,
} from "@/utils/gdriveUpload";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BAvatar,
    BSpinner,
    BFormGroup,
    BForm,
    BSidebar,
    BFormTextarea,
    BImg,
    BFormCheckbox,
    vSelect,
    flatPickr,
    BPagination,
    BBadge,
    BModal,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      visibility: false,
      sidebarTitle: "Add Circular",
      loading: false,
      submitting: false,
      docLoading: false,
      loadingClasses: false,
      searchQuery: "",

      // Form data
      myObj: {
        id: 0,
        campusID: this.headOfficeAvailable
          ? this.headOfficeCampusId
          : this.$store.state.userData?.cId ?? 0, // Default campus
        classIDs: [],
        secIDs: [],
        depID: [],
        circularFor: "all", // Set to "all" by default
        sIDs: "",
        tIDs: "",
        title: "",
        description: "",
        dtFrom: "",
        dtTo: "",
        attachment: "",
        metaData: "",
      },

      // Recipient types
      recipientTypes: [
        { label: "All", value: "all" },
        { label: "Student", value: "student" },
        { label: "Staff", value: "staff" },
      ],

      // Staff and Student data
      staffOptions: [],
      studentOptions: [],
      staffLoading: false,
      studentLoading: false,
      selectedStaffMembers: [], // Store full staff objects for extracting IDs and depIDs
      selectedStudentMembers: [], // Store full student objects for extracting IDs and classIDs/secIDs

      // Toggle switches
      selectSpecificStaff: false,
      selectSpecificStudents: false,

      // Dropdowns data
      campuses: [],
      classes: [],
      classOptions: [],
      sections: [],
      docList: [],
      departments: [], // Added departments for staff
      selectedCampuses: [], // Array to store selected campus IDs

      // Settings objects
      settObj: null,
      dtObj: null,
      stdObj: null,
      feePopup: null,
      headOfficeObj: null,

      // Date picker config
      dateConfig: {
        mode: "range",
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      dateRange: "",
      listFilters: {
        classID: null,
        circularFor: null,
        dtFrom: "",
        dtTo: "",
      },
      listDateRange: "",
      listDateConfig: {
        mode: "range",
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      perPage: 10,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],

      allClassesOption: { id: 0, name: "All Classes" },
      allSectionsOption: { id: 0, section: "All Sections" },
      allDepartmentsOption: { id: 0, name: "All Departments" },

      // View modal
      showViewModal: false,
      selectedItem: null,

      // Table data
      items: [],
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "col-sno text-center",
          tdClass: "col-sno text-center",
        },
        {
          key: "title",
          label: "Title",
          thClass: "col-title",
          tdClass: "col-title",
        },
        {
          key: "circularFor",
          label: "Recipient Type",
          thClass: "col-recipient-type",
          tdClass: "col-recipient-type",
        },
        {
          key: "metaData",
          label: "Recipients",
          thClass: "col-recipients",
          tdClass: "col-recipients",
        },
        {
          key: "campuses",
          label: "Campuses",
          thClass: "col-campuses",
          tdClass: "col-campuses",
        },
        {
          key: "dtFrom",
          label: "From Date",
          thClass: "col-date",
          tdClass: "col-date",
          formatter: (value) => {
            if (!value) return "";
            return new Date(value).toLocaleDateString();
          },
        },
        {
          key: "dtTo",
          label: "To Date",
          thClass: "col-date",
          tdClass: "col-date",
          formatter: (value) => {
            if (!value) return "";
            return new Date(value).toLocaleDateString();
          },
        },
        {
          key: "description",
          label: "Description",
          thClass: "col-description",
          tdClass: "col-description",
        },
        {
          key: "actions",
          label: "Actions",
          thClass: "col-actions text-center",
          tdClass: "col-actions text-center",
        },
      ],
      totalCount: 0,
      totalPages: 0,
      currentPage: 1,
    };
  },
  watch: {
    selectSpecificStaff(val) {
      if (val) {
        this.myObj.depID = [];
        this.loadStaffSuggestions();
      } else {
        this.selectedStaffMembers = [];
        this.myObj.tIDs = "";
        this.myObj.depID = [];
        this.staffOptions = [];
      }
    },
    selectSpecificStudents(val) {
      if (val) {
        this.myObj.classIDs = [];
        this.myObj.secIDs = [];
        this.sections = [];
        this.loadStudentSuggestions();
      } else {
        this.selectedStudentMembers = [];
        this.myObj.sIDs = "";
        this.myObj.classIDs = [];
        this.myObj.secIDs = [];
        this.studentOptions = [];
        this.sections = [];
      }
    },
  },
  created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.LoadCampuses();
    this.LoadDepartments();
    this.LoadData();
    this.loadSettings();
    this.LoadSettingsAll();
  },
  async mounted() {
    // Ensure campuses are loaded
    await this.LoadCampuses();
    // Ensure default values are set
    if (this.selectedCampuses.length === 0) {
      if (this.headOfficeAvailable) {
        // Default to "All" if head office is available
        this.selectedCampuses = [this.headOfficeCampusId];
        this.myObj.campusID = this.headOfficeCampusId;
      } else {
        // If no head_office configured, fallback to logged-in user's campusID
        const fallbackCampus = this.$store.state.userData?.cId ?? 0;
        this.selectedCampuses = [fallbackCampus];
        this.myObj.campusID = fallbackCampus;
      }
    }
    if (!this.myObj.circularFor) {
      this.myObj.circularFor = "all"; // Set "All" as default
    }
    // Auto-load classes when campus is preset/hidden
    await this.LoadClasses();
  },
  methods: {
    ...mapActions(["get", "post", "put", "delete"]),
    async loadStaffSuggestions() {
      this.staffLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}staff/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=a&status=present`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.staffOptions = (response || []).slice(0, 20).map((staff) => ({
          ...staff,
          displayName: staff.name || staff.staff_name || "Unknown Staff",
          name: staff.name || staff.staff_name || "Unknown Staff",
          designation: staff.designation || staff.title || "",
          department:
            staff.department || staff.department_name || staff.department || "",
          departmentID:
            staff.departmentID ||
            staff.department_id ||
            staff.departmentID ||
            "",
        }));
      } catch (e) {
        this.staffOptions = [];
      } finally {
        this.staffLoading = false;
      }
    },
    async loadStudentSuggestions() {
      if (this.selectedCampuses.length === 0) return;
      this.studentLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=a&type=`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.studentOptions = (response || []).slice(0, 20).map((student) => ({
          ...student,
          displayName:
            student.name || student.student_name || "Unknown Student",
          name: student.name || student.student_name || "Unknown Student",
          father_name: student.father_name || student.fatherName || "",
          mother_name: student.mother_name || student.motherName || "",
          roll_number: student.roll_number || student.rollNumber || "",
          className:
            student.className || student.class_name || student.class || "",
          sectionName:
            student.sectionName ||
            student.section_name ||
            student.section ||
            "",
          admission_number:
            student.admission_number || student.admissionNumber || "",
          phone: student.phone || student.phone_number || student.contact || "",
          address: student.address || student.full_address || "",
          gender: student.gender || student.sex || "",
          classID: student.classID || student.class_id || student.classID || "",
          sectionID:
            student.sectionID || student.section_id || student.sectionID || "",
          grNo: student.grNo || student.gr_no || student.gr_number || "",
        }));
      } catch (e) {
        this.studentOptions = [];
      } finally {
        this.studentLoading = false;
      }
    },

    onDateRangeChange(selectedDates) {
      if (selectedDates && selectedDates.length === 2) {
        // Format dates as Y-m-d for storage (flatpickr returns Date objects)
        const formatDate = (date) => {
          if (!date) return "";
          const d = new Date(date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };
        this.myObj.dtFrom = formatDate(selectedDates[0]);
        this.myObj.dtTo = formatDate(selectedDates[1]);
      } else {
        // Clear the dates if no valid selection
        this.myObj.dtFrom = "";
        this.myObj.dtTo = "";
      }
    },

    openSidebar(item = null) {
      this.resetForm();
      if (item) {
        this.sidebarTitle = "Edit Circular";
        this.myObj = { ...item };
        this.docList = parseDocUrls(this.myObj.attachment);

        // Set toggle switches based on existing data
        this.selectSpecificStaff =
          this.myObj.tIDs && this.myObj.tIDs.length > 0;
        this.selectSpecificStudents =
          this.myObj.sIDs && this.myObj.sIDs.length > 0;

        // Set date range for editing - format dates properly for flatpickr
        if (this.myObj.dtFrom && this.myObj.dtTo) {
          // Convert ISO strings or date strings to Date objects for flatpickr
          const fromDate = this.myObj.dtFrom.includes("T")
            ? new Date(this.myObj.dtFrom)
            : new Date(this.myObj.dtFrom + "T00:00:00");
          const toDate = this.myObj.dtTo.includes("T")
            ? new Date(this.myObj.dtTo)
            : new Date(this.myObj.dtTo + "T00:00:00");
          this.dateRange = [fromDate, toDate];
          // Also ensure dates are in Y-m-d format for consistency
          const formatDate = (dateStr) => {
            if (!dateStr) return "";
            const date = new Date(dateStr);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
          };
          this.myObj.dtFrom = formatDate(this.myObj.dtFrom);
          this.myObj.dtTo = formatDate(this.myObj.dtTo);
        }

        // Load staff data if specific staff are selected
        if (this.selectSpecificStaff && this.myObj.tIDs) {
          this.loadStaffForEdit(this.myObj.tIDs);
        }

        // Load student data if specific students are selected
        if (this.selectSpecificStudents && this.myObj.sIDs) {
          this.loadStudentsForEdit(this.myObj.sIDs);
        }

        // Keep depID=0 ("All Departments") as a real selected value
        if (
          this.myObj.circularFor === "staff" &&
          !this.selectSpecificStaff &&
          this.myObj.depID !== "" &&
          this.myObj.depID != null
        ) {
          this.myObj.depID = this.parseDepartmentIds(this.myObj.depID);
        } else if (
          this.myObj.circularFor === "staff" &&
          !this.selectSpecificStaff
        ) {
          this.myObj.depID = [];
        }

        // Handle campus selection for edit mode
        // If item has campusIDs, use those; otherwise use campusID
        if (item.campusIDs) {
          // Parse comma-separated campus IDs
          const campusIds = item.campusIDs
            .split(",")
            .map((id) => parseInt(id.trim()))
            .filter((id) => !isNaN(id));
          this.selectedCampuses = campusIds;
          // Check if "All" should be selected (if all campuses are selected)
          if (
            this.headOfficeAvailable &&
            campusIds.length ===
              this.campuses.filter((c) => c.id !== this.headOfficeCampusId)
                .length
          ) {
            this.selectedCampuses = [this.headOfficeCampusId, ...campusIds];
          }
          this.myObj.campusID =
            this.selectedCampuses[0] || this.headOfficeCampusId;
        } else if (item.campusID) {
          // Single campus ID
          this.selectedCampuses = [item.campusID];
          this.myObj.campusID = item.campusID;
        }

        // Load classes if campus is selected and not "All"
        if (this.selectedCampuses.length > 0 && !this.isAllCampusesSelected) {
          this.LoadClasses();
        }
      } else {
        this.sidebarTitle = "Add Circular";
        this.selectSpecificStaff = false;
        this.selectSpecificStudents = false;
      }
      this.visibility = true;
    },

    resetForm() {
      this.myObj = {
        id: 0,
        campusID: this.headOfficeAvailable
          ? this.headOfficeCampusId
          : this.$store.state.userData?.cId ?? 0, // Default campus
        classIDs: [],
        secIDs: [],
        depID: [],
        circularFor: "all", // Set to "all" by default
        sIDs: "",
        tIDs: "",
        title: "",
        description: "",
        dtFrom: "",
        dtTo: "",
        attachment: "",
        metaData: "",
      };
      this.docList = [];
      this.staffOptions = [];
      this.studentOptions = [];
      this.selectedStaffMembers = [];
      this.selectedStudentMembers = [];
      this.dateRange = "";
      this.selectSpecificStaff = false;
      this.selectSpecificStudents = false;

      // Reset selected campuses
      if (this.headOfficeAvailable) {
        this.selectedCampuses = [this.headOfficeCampusId];
      } else {
        this.selectedCampuses = [this.$store.state.userData?.cId ?? 0];
      }

      // Reset form validation states
      this.$nextTick(() => {
        if (this.$refs.campus) this.$refs.campus.state = null;
        if (this.$refs.recipientType) this.$refs.recipientType.state = null;
        if (this.$refs.title) this.$refs.title.state = null;
        if (this.$refs.dateRange) this.$refs.dateRange.state = null;
        if (this.$refs.description) this.$refs.description.state = null;
        if (this.$refs.staffSelection) this.$refs.staffSelection.state = null;
        if (this.$refs.studentSelection)
          this.$refs.studentSelection.state = null;
        if (this.$refs.department) this.$refs.department.state = null;
        if (this.$refs.class) this.$refs.class.state = null;
        if (this.$refs.section) this.$refs.section.state = null;
      });
      // Auto-load classes with the default campus
      this.LoadClasses();
    },

    onCampusChange(selectedCampusIds) {
      if (!selectedCampusIds || selectedCampusIds.length === 0) {
        this.selectedCampuses = [];
        this.myObj.campusID = this.headOfficeAvailable
          ? this.headOfficeCampusId
          : this.$store.state.userData?.cId ?? 0;
        this.LoadClasses();
        return;
      }

      // Check if "All" was previously selected
      const wasAllSelected = this.selectedCampuses.includes(
        this.headOfficeCampusId
      );
      const isAllSelected = selectedCampusIds.includes(this.headOfficeCampusId);

      // Get all campus IDs except "All"
      const allCampusIds = this.campuses
        .filter((c) => c.id !== this.headOfficeCampusId)
        .map((c) => c.id);

      // Check if all campuses are currently selected (meaning "All" is effectively selected)
      const allCampusesWereSelected =
        wasAllSelected &&
        this.selectedCampuses.length === allCampusIds.length + 1 &&
        allCampusIds.every((id) => this.selectedCampuses.includes(id));

      if (isAllSelected && !wasAllSelected) {
        // User just selected "All" - select all campuses
        this.selectedCampuses = [this.headOfficeCampusId, ...allCampusIds];
      } else if (wasAllSelected && !isAllSelected) {
        // User explicitly deselected "All" - keep only the other selected campuses
        this.selectedCampuses = selectedCampusIds.filter(
          (id) => id !== this.headOfficeCampusId
        );
      } else if (allCampusesWereSelected && isAllSelected) {
        // User had "All" selected (all campuses selected) and is now changing selection
        const selectedWithoutAll = selectedCampusIds.filter(
          (id) => id !== this.headOfficeCampusId
        );
        // If not all campuses are selected anymore, remove "All"
        if (selectedWithoutAll.length < allCampusIds.length) {
          // User is deselecting some campuses - remove "All"
          this.selectedCampuses = selectedWithoutAll;
        } else {
          // All campuses still selected, keep "All"
          this.selectedCampuses = [this.headOfficeCampusId, ...allCampusIds];
        }
      } else if (wasAllSelected && isAllSelected) {
        // "All" is in both old and new selection
        // Check if all campuses are still selected
        const selectedWithoutAll = selectedCampusIds.filter(
          (id) => id !== this.headOfficeCampusId
        );
        if (
          selectedWithoutAll.length === allCampusIds.length &&
          allCampusIds.every((id) => selectedWithoutAll.includes(id))
        ) {
          // All campuses still selected, keep "All"
          this.selectedCampuses = [this.headOfficeCampusId, ...allCampusIds];
        } else {
          // Not all campuses selected, remove "All"
          this.selectedCampuses = selectedWithoutAll;
        }
      } else {
        // Normal selection/deselection
        this.selectedCampuses = selectedCampusIds;
      }

      // Final check: if "All" is in selection but not all campuses are selected, remove "All"
      if (this.selectedCampuses.includes(this.headOfficeCampusId)) {
        const selectedWithoutAll = this.selectedCampuses.filter(
          (id) => id !== this.headOfficeCampusId
        );
        if (
          selectedWithoutAll.length < allCampusIds.length ||
          !allCampusIds.every((id) => selectedWithoutAll.includes(id))
        ) {
          // Not all campuses are selected, remove "All"
          this.selectedCampuses = selectedWithoutAll;
        }
      }

      // Update myObj.campusID for backward compatibility (use first selected or "All")
      if (this.selectedCampuses.length > 0) {
        this.myObj.campusID = this.selectedCampuses.includes(
          this.headOfficeCampusId
        )
          ? this.headOfficeCampusId
          : this.selectedCampuses[0];
      } else {
        this.myObj.campusID = this.headOfficeAvailable
          ? this.headOfficeCampusId
          : this.$store.state.userData?.cId ?? 0;
      }

      // Reset toggles and selections when campus changes
      this.selectSpecificStaff = false;
      this.selectSpecificStudents = false;
      this.selectedStaffMembers = [];
      this.selectedStudentMembers = [];
      this.myObj.sIDs = "";
      this.myObj.tIDs = "";
      this.staffOptions = [];
      this.studentOptions = [];
      this.LoadClasses();
    },

    onRecipientTypeChange() {
      // Reset selections when recipient type changes
      this.myObj.sIDs = "";
      this.myObj.tIDs = "";
      this.myObj.depID = []; // Reset department for staff
      this.myObj.classIDs = [];
      this.myObj.secIDs = [];
      this.sections = [];
      this.staffOptions = [];
      this.studentOptions = [];
      this.selectedStaffMembers = [];
      this.selectedStudentMembers = [];
      this.selectSpecificStaff = false; // Reset toggle
      this.selectSpecificStudents = false; // Reset toggle
      // If selecting students and campus is preselected/hidden, ensure classes are loaded
      if (this.myObj.circularFor === "student") {
        this.LoadClasses();
      }
    },

    onDepartmentChange() {
      this.myObj.depID = this.applyAllOptionSelection(this.myObj.depID);
    },

    onClassChange() {
      this.myObj.classIDs = this.applyAllOptionSelection(this.myObj.classIDs);
      this.myObj.secIDs = [];
      this.buildSectionsForClasses(this.myObj.classIDs);
    },

    onSectionChange() {
      this.myObj.secIDs = this.applyAllOptionSelection(this.myObj.secIDs);
    },

    applyAllOptionSelection(value) {
      let ids = Array.isArray(value) ? [...value] : this.parseIdList(value);
      if (!ids.length) return [];
      if (ids.at(-1) == 0) return [0];
      return ids.filter((id) => id != 0);
    },

    parseDepartmentIds(value) {
      return this.parseIdList(value);
    },

    parseIdList(value) {
      if (Array.isArray(value)) {
        return value
          .map((id) => this.normalizeFilterId(id))
          .filter(
            (id) =>
              id === 0 || (id !== "" && id != null && !Number.isNaN(Number(id)))
          );
      }
      if (value === 0 || value === "0") return [0];
      if (value === "" || value == null) return [];
      return String(value)
        .split(",")
        .map((id) => this.normalizeFilterId(id.trim()))
        .filter(
          (id) =>
            id === 0 || (id !== "" && id != null && !Number.isNaN(Number(id)))
        );
    },

    async searchStaff(search) {
      if (search.length < 2) return;

      this.staffLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}staff/Search?db=${
            this.$store.state.userData.db
          }&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(
            search
          )}&status=present`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);

        // Ensure we have comprehensive staff data
        this.staffOptions = (response || []).map((staff) => ({
          ...staff,
          // Ensure displayName is set for v-select
          displayName: staff.name || staff.staff_name || "Unknown Staff",
          // Normalize field names for consistency
          name: staff.name || staff.staff_name || "Unknown Staff",
          designation:
            staff.designation || staff.designation || staff.title || "",
          department:
            staff.department || staff.department_name || staff.department || "",
          departmentID:
            staff.departmentID ||
            staff.department_id ||
            staff.departmentID ||
            "",
        }));
      } catch (error) {
        console.error("Staff search error:", error);
        this.staffOptions = [];
        this.$bvToast.toast("Failed to search staff. Please try again.", {
          title: "Search Error",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.staffLoading = false;
      }
    },

    // Handle staff selection to extract both tIDs and depIDs
    onStaffSelectionChange(selectedStaff) {
      if (selectedStaff && selectedStaff.length > 0) {
        // Extract staff IDs
        this.myObj.tIDs = selectedStaff.map((staff) => staff.id);
        // Extract unique department IDs
        const uniqueDepIDs = [
          ...new Set(
            selectedStaff.map((staff) => staff.departmentID).filter((id) => id)
          ),
        ];
        this.myObj.depID = uniqueDepIDs.join(",");
      } else {
        this.myObj.tIDs = [];
        this.myObj.depID = [];
      }
    },

    // Remove individual staff from selection
    removeStaff(index) {
      this.selectedStaffMembers.splice(index, 1);
      this.onStaffSelectionChange(this.selectedStaffMembers);
    },

    // Handle student selection to extract sIDs, classIDs, and secIDs
    onStudentSelectionChange(selectedStudents) {
      if (selectedStudents && selectedStudents.length > 0) {
        // Extract student IDs
        this.myObj.sIDs = selectedStudents.map((student) => student.id);
        // Extract unique class IDs
        const uniqueClassIDs = [
          ...new Set(
            selectedStudents
              .map((student) => student.classID)
              .filter((id) => id)
          ),
        ];
        this.myObj.classIDs = uniqueClassIDs.join(",");
        // Extract unique section IDs
        const uniqueSecIDs = [
          ...new Set(
            selectedStudents
              .map((student) => student.sectionID)
              .filter((id) => id)
          ),
        ];
        this.myObj.secIDs = uniqueSecIDs.join(",");
      } else {
        this.myObj.sIDs = [];
        this.myObj.classIDs = [];
        this.myObj.secIDs = [];
      }
    },

    // Remove individual student from selection
    removeStudent(index) {
      this.selectedStudentMembers.splice(index, 1);
      this.onStudentSelectionChange(this.selectedStudentMembers);
    },

    async searchStudents(search) {
      if (this.selectedCampuses.length === 0 || search.length < 2) return;

      this.studentLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/Search?db=${
            this.$store.state.userData.db
          }&cID=${this.$store.state.userData.cId}&keyword=${encodeURIComponent(
            search
          )}&type=`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);

        // Ensure we have comprehensive student data
        this.studentOptions = (response || []).map((student) => ({
          ...student,
          // Ensure displayName is set for v-select
          displayName:
            student.name || student.student_name || "Unknown Student",
          // Normalize field names for consistency
          name: student.name || student.student_name || "Unknown Student",
          father_name:
            student.father_name ||
            student.fatherName ||
            student.father_name ||
            "",
          mother_name:
            student.mother_name ||
            student.motherName ||
            student.mother_name ||
            "",
          roll_number:
            student.roll_number ||
            student.rollNumber ||
            student.roll_number ||
            "",
          className:
            student.className || student.class_name || student.class || "",
          sectionName:
            student.sectionName ||
            student.section_name ||
            student.section ||
            "",
          admission_number:
            student.admission_number ||
            student.admissionNumber ||
            student.admission_number ||
            "",
          phone: student.phone || student.phone_number || student.contact || "",
          address: student.address || student.full_address || "",
          gender: student.gender || student.sex || "",
          classID: student.classID || student.class_id || student.classID || "",
          sectionID:
            student.sectionID || student.section_id || student.sectionID || "",
          grNo: student.grNo || student.gr_no || student.gr_number || "", // Added grNo
        }));
      } catch (error) {
        console.error("Student search error:", error);
        this.studentOptions = [];
        this.$bvToast.toast("Failed to search students. Please try again.", {
          title: "Search Error",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.studentLoading = false;
      }
    },

    async loadStudents() {
      if (!this.hasStudentSectionSelected) return;

      this.studentLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Students/LoadStudents?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&classID=${this.myObj.classIDs}&sectionID=${this.myObj.secIDs}`,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);

        // Ensure we have comprehensive student data
        this.studentOptions = (response || []).map((student) => ({
          ...student,
          // Ensure displayName is set for v-select
          displayName:
            student.name || student.student_name || "Unknown Student",
          // Normalize field names for consistency
          name: student.name || student.student_name || "Unknown Student",
          father_name:
            student.father_name ||
            student.fatherName ||
            student.father_name ||
            "",
          mother_name:
            student.mother_name ||
            student.motherName ||
            student.mother_name ||
            "",
          roll_number:
            student.roll_number ||
            student.rollNumber ||
            student.roll_number ||
            "",
          className:
            student.className || student.class_name || student.class || "",
          sectionName:
            student.sectionName ||
            student.section_name ||
            student.section ||
            "",
          admission_number:
            student.admission_number ||
            student.admissionNumber ||
            student.admission_number ||
            "",
          phone: student.phone || student.phone_number || student.contact || "",
          address: student.address || student.full_address || "",
          gender: student.gender || student.sex || "",
          classID: student.classID || student.class_id || student.classID || "",
          sectionID:
            student.sectionID || student.section_id || student.sectionID || "",
          grNo: student.grNo || student.gr_no || student.gr_number || "", // Added grNo
        }));
      } catch (error) {
        console.error("Student load error:", error);
        this.studentOptions = [];
        this.$bvToast.toast("Failed to load students. Please try again.", {
          title: "Load Error",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.studentLoading = false;
      }
    },

    async LoadCampuses() {
      const obj = {
        url: `${this.$store.state.domain}campuses?db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}`,
        token: this.$store.state.userData.token,
      };
      this.campuses = await this.get(obj);

      // Determine Head Office campus ID from settings if available
      const headOfficeId =
        this.headOfficeObj &&
        (this.headOfficeObj.valueNumber ??
          this.headOfficeObj.value ??
          this.headOfficeObj.valueInt);

      // Build options
      if (
        headOfficeId !== null &&
        headOfficeId !== undefined &&
        headOfficeId !== ""
      ) {
        // Remove any existing campus with the same ID as headOfficeId to avoid duplicates
        // Also remove any existing 'All' option by branch name
        this.campuses = (this.campuses || []).filter((c) => {
          const isAllBranch = (c?.branch || "").toLowerCase() === "all";
          const isHeadOfficeId = c.id === headOfficeId;
          return !isAllBranch && !isHeadOfficeId;
        });

        // Remove any duplicate IDs (keep first occurrence)
        const seenIds = new Set();
        this.campuses = this.campuses.filter((c) => {
          if (seenIds.has(c.id)) {
            return false;
          }
          seenIds.add(c.id);
          return true;
        });

        // Prepend "All" option
        this.campuses = [{ id: headOfficeId, branch: "All" }, ...this.campuses];

        // Default to head office if not set
        if (this.selectedCampuses.length === 0) {
          this.selectedCampuses = [headOfficeId];
          this.myObj.campusID = headOfficeId;
        }
      } else {
        // No head_office setting, use user's campus only
        const userCampusId = this.$store.state.userData?.cId ?? 0;

        // Remove any duplicate IDs (keep first occurrence)
        const seenIds = new Set();
        this.campuses = (this.campuses || []).filter((c) => {
          if (seenIds.has(c.id)) {
            return false;
          }
          seenIds.add(c.id);
          return true;
        });

        // Ensure user's campus exists in options; if not, inject a placeholder
        const hasUserCampus = this.campuses.some((c) => c.id === userCampusId);
        if (!hasUserCampus) {
          this.campuses = [
            { id: userCampusId, branch: "Current campus" },
            ...this.campuses,
          ];
        }
        if (this.selectedCampuses.length === 0) {
          this.selectedCampuses = [userCampusId];
          this.myObj.campusID = userCampusId;
        }
        // Since dropdown is hidden, load classes for user's campus
        await this.LoadClasses();
      }
    },

    async LoadDepartments() {
      const obj = {
        url: `${this.$store.state.domain}StaffDepartments?db=${this.$store.state.userData.db}&cId=${this.$store.state.userData.cId}`,
        token: this.$store.state.userData.token,
      };
      const res = await this.get(obj);
      const departmentList = (Array.isArray(res) ? res : []).map((dept) => ({
        ...dept,
        name: dept.name || dept.department || `Department ${dept.id}`,
      }));
      this.departments = [this.allDepartmentsOption, ...departmentList];
    },

    async loadSettings() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: [
          "multi_whatsapp",
          "department-timings",
          "studentTimings",
          "head_office",
        ],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      // console.log(res);
      this.settObj = res.find((el) => el.key == "multi_whatsapp");
      this.dtObj = res.find((el) => el.key == "department-timings");
      this.stdObj = res.find((el) => el.key == "studentTimings");
      this.headOfficeObj = res.find((el) => el.key == "head_office");
      // After loading head office, refresh campuses so 'All' uses right ID
      await this.LoadCampuses();
      // console.log(this.settObj);
    },

    async LoadSettingsAll() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettingsAll?db=" +
          this.$store.state.userData.db,
        body: ["update_fee_popup"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });
      this.feePopup = res.find((el) => el.key == "update_fee_popup");
    },

    async LoadClasses() {
      const isEdit = Number(this.myObj.id) > 0;
      const savedClassIDs = isEdit ? this.myObj.classIDs : null;
      const savedSecIDs = isEdit ? this.myObj.secIDs : null;

      if (!isEdit) {
        this.myObj.classIDs = [];
        this.myObj.secIDs = [];
        this.sections = [];
      }
      this.classOptions = [];
      this.loadingClasses = true;

      const campusIdToUse = this.$store.state.userData?.cId ?? 0;

      if (!campusIdToUse && campusIdToUse !== 0) {
        this.loadingClasses = false;
        return;
      }

      const obj = {
        url: `${this.$store.state.domain}myclasses/LoadClasses?db=${this.$store.state.userData.db}&cId=${campusIdToUse}&dID=0`,
        token: this.$store.state.userData.token,
      };
      const res = await this.get(obj);
      this.classes = res;
      this.classOptions = [
        this.allClassesOption,
        ...this.classes.map((el) => el.cls),
      ];

      if (
        isEdit &&
        !this.selectSpecificStudents &&
        savedClassIDs !== "" &&
        savedClassIDs != null &&
        !(Array.isArray(savedClassIDs) && !savedClassIDs.length)
      ) {
        this.myObj.classIDs = this.parseIdList(savedClassIDs);
        this.buildSectionsForClasses(this.myObj.classIDs, savedSecIDs);
      }

      this.loadingClasses = false;
    },

    normalizeFilterId(value) {
      if (value === 0 || value === "0") return 0;
      const n = Number(value);
      return Number.isNaN(n) ? value : n;
    },

    updateSections() {
      this.myObj.secIDs = [];
      this.buildSectionsForClasses(this.myObj.classIDs);
    },

    buildSectionsForClasses(classIds, presetSecId = null) {
      const ids = Array.isArray(classIds)
        ? classIds
        : this.parseIdList(classIds);

      if (!ids.length) {
        this.sections = [];
        this.myObj.secIDs = [];
        return;
      }

      // All Classes → only All Sections
      if (ids.includes(0)) {
        this.sections = [this.allSectionsOption];
        this.myObj.secIDs =
          presetSecId != null && presetSecId !== ""
            ? this.parseIdList(presetSecId)
            : [0];
        if (!this.myObj.secIDs.length) this.myObj.secIDs = [0];
        return;
      }

      const sectionMap = new Map();
      ids.forEach((classId) => {
        const selectedClass = this.classes.find((el) => el.cls.id == classId);
        (selectedClass?.sections || []).forEach((sec) => {
          if (sec && sec.id != null && !sectionMap.has(sec.id)) {
            sectionMap.set(sec.id, sec);
          }
        });
      });

      this.sections = [
        this.allSectionsOption,
        ...Array.from(sectionMap.values()),
      ];

      if (presetSecId != null && presetSecId !== "") {
        const parsedSecs = this.parseIdList(presetSecId);
        const validIds = new Set(this.sections.map((s) => s.id));
        this.myObj.secIDs = parsedSecs.filter((id) => validIds.has(id));
        if (!this.myObj.secIDs.length && parsedSecs.includes(0)) {
          this.myObj.secIDs = [0];
        }
      }
    },

    buildSectionsForClass(classId, presetSecId = null) {
      this.buildSectionsForClasses(classId, presetSecId);
    },
    isPreviewableDoc(entry) {
      return checkPreviewableDoc(entry);
    },
    getDocPreviewUrl(entry) {
      return resolveDocPreviewUrl(entry);
    },
    getDocUrl(entry) {
      return resolveDocUrl(entry);
    },
    onDocPreviewError(event, entry) {
      const doc = typeof entry === "object" ? entry : { url: entry };
      handleDrivePreviewError(event, doc.url, doc.type);
    },

    openFile(url) {
      window.open(url, "_blank");
    },

    async addDoc() {
      if (this.$refs.noticeDocs.files.length > 0) {
        const docs = this.$refs.noticeDocs.files;
        this.docLoading = true;

        try {
          const myresp = await uploadToMySkool(docs);
          if (!myresp.length) {
            throw new Error("No files were uploaded");
          }

          this.docList.push(
            ...myresp.map((item) => buildUploadedAttachment(item))
          );
          this.myObj.attachment = serializeDocUrls(this.docList);

          this.$bvToast.toast("File uploaded successfully!", {
            title: "Success!",
            variant: "success",
            toaster: "b-toaster-top-center",
          });
        } catch (error) {
          console.error("Upload error:", error);
          this.$bvToast.toast("Failed to upload file", {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          });
        } finally {
          this.docLoading = false;
        }
      }
    },

    removeDoc(index) {
      this.docList.splice(index, 1);
      this.myObj.attachment = serializeDocUrls(this.docList);
    },

    validateForm() {
      let isValid = true;
      const refs = ["title", "description"];

      refs.forEach((ref) => {
        const elem = this.$refs[ref];
        if (
          !this.myObj[ref] ||
          (Array.isArray(this.myObj[ref]) && this.myObj[ref].length === 0)
        ) {
          elem && (elem.state = false);
          isValid = false;
        } else {
          elem && (elem.state = true);
        }
      });

      // Validate campus (guard when dropdown is hidden)
      const hasCampus = this.selectedCampuses.length > 0;
      if (!hasCampus) {
        if (this.$refs.campus) this.$refs.campus.state = false;
        isValid = false;
      } else {
        if (this.$refs.campus) this.$refs.campus.state = true;
      }

      // Validate date range
      if (!this.dateRange || !this.myObj.dtFrom || !this.myObj.dtTo) {
        if (this.$refs.dateRange) this.$refs.dateRange.state = false;
        isValid = false;
      } else {
        if (this.$refs.dateRange) this.$refs.dateRange.state = true;
      }

      // Validate recipient selection
      if (this.myObj.circularFor === "staff") {
        // Skip validation for department/staff when multiple campuses are selected
        if (this.isMultipleCampusesSelected) {
          // Only validate campus selection for multiple campuses
          if (!hasCampus) {
            if (this.$refs.campus) this.$refs.campus.state = false;
            isValid = false;
          }
        } else if (this.selectSpecificStaff) {
          if (
            !this.selectedStaffMembers ||
            this.selectedStaffMembers.length === 0
          ) {
            if (this.$refs.staffSelection)
              this.$refs.staffSelection.state = false;
            isValid = false;
          }
        } else {
          // Allow 0 ("All Departments") — do not treat it as empty
          if (!this.hasDepartmentSelected) {
            if (this.$refs.department) this.$refs.department.state = false;
            isValid = false;
          } else if (this.$refs.department) {
            this.$refs.department.state = true;
          }
        }
      } else if (this.myObj.circularFor === "student") {
        // Skip validation for class/section/students when multiple campuses are selected
        if (this.isMultipleCampusesSelected) {
          // Only validate campus selection for multiple campuses
          if (!hasCampus) {
            if (this.$refs.campus) this.$refs.campus.state = false;
            isValid = false;
          }
        } else if (this.selectSpecificStudents) {
          if (
            !this.selectedStudentMembers ||
            this.selectedStudentMembers.length === 0
          ) {
            if (this.$refs.studentSelection)
              this.$refs.studentSelection.state = false;
            isValid = false;
          }
        } else {
          // For students with toggle OFF, campus, class, and section are required
          if (!hasCampus) {
            if (this.$refs.campus) this.$refs.campus.state = false;
            isValid = false;
          }
          if (!this.hasStudentClassSelected) {
            if (this.$refs.class) this.$refs.class.state = false;
            isValid = false;
          } else if (this.$refs.class) this.$refs.class.state = true;

          if (!this.hasStudentSectionSelected) {
            if (this.$refs.section) this.$refs.section.state = false;
            isValid = false;
          } else if (this.$refs.section) this.$refs.section.state = true;
        }
      }

      return isValid;
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        this.$bvToast.toast("Please fill all required fields", {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      // Prepare the data for API
      const circularData = {
        ...this.myObj,
        // Always use logged-in user's campus ID, not the selected one
        campusID: this.$store.state.userData?.cId ?? 0,
        // Convert arrays to comma-separated strings and ensure string format
        sIDs: Array.isArray(this.myObj.sIDs)
          ? this.myObj.sIDs.join(",")
          : this.myObj.sIDs
          ? this.myObj.sIDs.toString()
          : "",
        tIDs: Array.isArray(this.myObj.tIDs)
          ? this.myObj.tIDs.join(",")
          : this.myObj.tIDs
          ? this.myObj.tIDs.toString()
          : "",
        classIDs: Array.isArray(this.myObj.classIDs)
          ? this.myObj.classIDs.join(",")
          : this.hasStudentClassSelected
          ? this.myObj.classIDs.toString()
          : "",
        secIDs: Array.isArray(this.myObj.secIDs)
          ? this.myObj.secIDs.join(",")
          : this.hasStudentSectionSelected
          ? this.myObj.secIDs.toString()
          : "",
        depID: this.hasDepartmentSelected
          ? Array.isArray(this.myObj.depID)
            ? this.myObj.depID.join(",")
            : this.myObj.depID.toString()
          : "",
        // Pass selected campus IDs as campusIDs (comma-separated)
        campusIDs: this.allCampusIDs,
        // Handle date range - ensure dates are in Y-m-d format, then convert to ISO at midnight UTC
        dtFrom: this.myObj.dtFrom
          ? this.myObj.dtFrom.includes("T")
            ? new Date(this.myObj.dtFrom).toISOString()
            : new Date(this.myObj.dtFrom + "T00:00:00Z").toISOString()
          : new Date().toISOString(),
        dtTo: this.myObj.dtTo
          ? this.myObj.dtTo.includes("T")
            ? new Date(this.myObj.dtTo).toISOString()
            : new Date(this.myObj.dtTo + "T23:59:59Z").toISOString()
          : new Date().toISOString(),
      };

      console.log("Circular data:", circularData);

      this.submitting = true;
      try {
        const url = `${this.$store.state.domain}Circulars/SaveCircular?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`;
        const status = await this.post({
          url,
          body: circularData,
          token: this.$store.state.userData.token,
          message: `Circular ${
            this.myObj.id > 0 ? "updated" : "added"
          } successfully!`,
          context: this,
        });

        if (status) {
          this.visibility = false;
          this.LoadData();
        }
      } catch (error) {
        console.error("Submit error:", error);
      } finally {
        this.submitting = false;
      }
    },

    buildListUrl(page, pageSize) {
      const params = new URLSearchParams({
        db: this.$store.state.userData.db,
        cID: String(this.$store.state.userData.cId),
        page: String(page),
        pageSize: String(pageSize),
      });

      if (
        this.listFilters.classID !== null &&
        this.listFilters.classID !== ""
      ) {
        params.set("classID", String(this.listFilters.classID));
      }
      if (this.listFilters.circularFor) {
        params.set("circularFor", this.listFilters.circularFor);
      }
      if (this.listFilters.dtFrom) {
        params.set("dtFrom", this.listFilters.dtFrom);
      }
      if (this.listFilters.dtTo) {
        params.set("dtTo", this.listFilters.dtTo);
      }

      return (
        this.$store.state.domain +
        "Circulars/LoadAllCirculars?" +
        params.toString()
      );
    },

    onListFilterChange() {
      this.currentPage = 1;
      this.LoadData(1, this.perPage);
    },

    onListDateRangeChange(selectedDates) {
      if (selectedDates && selectedDates.length === 2) {
        this.listFilters.dtFrom = this.formatDateForInput(selectedDates[0]);
        this.listFilters.dtTo = this.formatDateForInput(selectedDates[1]);
        this.onListFilterChange();
      } else if (!selectedDates || selectedDates.length === 0) {
        if (this.listFilters.dtFrom || this.listFilters.dtTo) {
          this.listFilters.dtFrom = "";
          this.listFilters.dtTo = "";
          this.onListFilterChange();
        }
      }
    },

    formatDateForInput(date) {
      const d = new Date(date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    clearListFilters() {
      this.listFilters = {
        classID: null,
        circularFor: null,
        dtFrom: "",
        dtTo: "",
      };
      this.listDateRange = "";
      this.currentPage = 1;
      this.LoadData(1, this.perPage);
    },

    onPageChange(page) {
      this.LoadData(page, this.perPage);
    },

    onPerPageChange() {
      this.currentPage = 1;
      this.LoadData(1, this.perPage);
    },

    async LoadData(page = 1, pageSize = null) {
      const size = pageSize ?? this.perPage;
      this.loading = true;
      try {
        const obj = {
          url: this.buildListUrl(page, size),
          token: this.$store.state.userData.token,
          returnData: true,
        };
        const response = await this.get(obj);
        if (response && Array.isArray(response.data)) {
          this.items = response.data;
          this.totalCount = Number(
            response.count ?? response.totalCount ?? response.data.length ?? 0
          );
          this.currentPage = Number(response.page ?? page);
          this.perPage = Number(response.pageSize ?? size);
          this.totalPages = Number(
            response.totalPages ??
              Math.ceil(this.totalCount / (this.perPage || 1))
          );
        } else if (Array.isArray(response)) {
          this.items = response;
          this.totalCount = response.length;
          this.currentPage = page;
          this.perPage = size;
          this.totalPages =
            Math.ceil(this.totalCount / (this.perPage || 1)) || 0;
        } else {
          this.items = [];
          this.totalCount = 0;
          this.totalPages = 0;
          this.currentPage = 1;
        }
      } catch (error) {
        console.error("Load data error:", error);
        this.items = [];
        this.totalCount = 0;
        this.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },

    async deleteItem(item) {
      const result = await this.$swal({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        try {
          const status = await this.delete({
            url: `https://apiv26.myskool.app/api/Circulars/${item.id}?db=${this.$store.state.userData.db}`,
            token: this.$store.state.userData.token,
            message: "Circular deleted successfully!",
            context: this,
          });

          if (status) {
            this.LoadData();
          }
        } catch (error) {
          console.error("Delete error:", error);
        }
      }
    },

    editItem(item) {
      this.openSidebar(item);
    },

    viewItem(item) {
      this.selectedItem = item;
      this.showViewModal = true;
    },

    getRecipientDisplay(item) {
      if (!item) return "";

      const meta = item.metaData && String(item.metaData).trim();
      if (meta) return meta;

      if (item.circularFor === "all") return "All";

      if (item.circularFor === "staff") {
        if (item.tIDs && String(item.tIDs).trim()) {
          const count = String(item.tIDs).split(",").filter(Boolean).length;
          return count ? `${count} staff selected` : "Staff";
        }
        if (
          item.depID !== null &&
          item.depID !== undefined &&
          String(item.depID).trim() !== ""
        ) {
          const ids = String(item.depID)
            .split(",")
            .map((id) => id.trim())
            .filter((id) => id !== "");
          if (!ids.length) return "All Staff";
          if (ids.length === 1 && (ids[0] === "0" || ids[0] === 0)) {
            return "All Departments";
          }
          const names = ids
            .map((id) => {
              if (id === "0") return "All Departments";
              const dept = this.departments.find(
                (d) => String(d.id) === String(id)
              );
              return dept
                ? dept.department || dept.name || `Department ${id}`
                : `Department ${id}`;
            })
            .filter(Boolean);
          if (!names.length) return `Department ${ids.join(", ")}`;
          if (names.length === 1) return names[0];
          if (names.length <= 3) return names.join(", ");
          return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
        }
        return "All Staff";
      }

      if (item.circularFor === "student") {
        if (item.sIDs && String(item.sIDs).trim()) {
          const count = String(item.sIDs).split(",").filter(Boolean).length;
          return count ? `${count} students selected` : "Students";
        }
        return this.formatStudentRecipient(item.classIDs, item.secIDs);
      }

      return "";
    },

    formatStudentRecipient(classIDs, secIDs) {
      const classLabel = this.formatClassIdsLabel(classIDs);
      const classIsAll =
        classIDs === 0 ||
        classIDs === "0" ||
        String(classIDs)
          .split(",")
          .every((id) => id.trim() === "0");

      if (classIsAll) return classLabel || "All Classes";

      const sectionLabel = this.formatSectionIdsLabel(classIDs, secIDs);
      const parts = [classLabel, sectionLabel].filter(Boolean);
      return parts.length ? parts.join(", ") : "Students";
    },

    formatClassIdsLabel(classIDs) {
      if (classIDs === null || classIDs === undefined || classIDs === "") {
        return "";
      }

      const ids = String(classIDs)
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "");

      if (!ids.length) return "";

      return ids
        .map((id) => {
          if (id === "0") return "All Classes";
          const cls =
            this.classOptions.find((c) => c.id == id) ||
            this.classes.find((el) => el.cls?.id == id)?.cls;
          return cls?.name || `Class ${id}`;
        })
        .join(", ");
    },

    formatSectionIdsLabel(classIDs, secIDs) {
      if (secIDs === null || secIDs === undefined || secIDs === "") {
        return "";
      }
      if (secIDs === 0 || secIDs === "0") return "";

      const ids = String(secIDs)
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "" && s !== "0");

      if (!ids.length) return "";

      return ids
        .map((id) => {
          for (const entry of this.classes) {
            const sec = (entry.sections || []).find((s) => s.id == id);
            if (sec) return sec.section;
          }
          return `Section ${id}`;
        })
        .join(", ");
    },

    getTruncatedRecipients(item) {
      const text = this.getRecipientDisplay(item);
      if (!text) return "—";
      if (text.length <= 50) return text;
      return `${text.substring(0, 50)}...`;
    },

    getTruncatedCampuses(metaCampusName) {
      if (!metaCampusName) return "N/A";

      const campuses = metaCampusName
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c);
      if (campuses.length === 0) return "N/A";

      // Show first 3 campuses, then "..."
      if (campuses.length <= 3) {
        return campuses.join(", ");
      }

      return campuses.slice(0, 3).join(", ") + "...";
    },

    getCampusList(metaCampusName) {
      if (!metaCampusName) return [];
      return metaCampusName
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c);
    },

    // Load staff data for editing when tIDs are available
    async loadStaffForEdit(tIDsString) {
      if (!tIDsString) return;

      const staffIDs = tIDsString.split(",");
      this.staffLoading = true;

      try {
        // We'll need to search for each staff member
        // Since we don't have a direct API to get staff by IDs, we'll use a workaround
        const allStaffPromises = staffIDs.map(async (staffID) => {
          try {
            const obj = {
              url: `${this.$store.state.domain}staff/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${staffID}&status=present`,
              token: this.$store.state.userData.token,
            };
            const response = await this.get(obj);
            // Find the exact staff member by ID
            const staff = response.find(
              (staff) => staff.id.toString() === staffID.toString()
            );

            if (staff) {
              // Normalize the staff data
              return {
                ...staff,
                displayName: staff.name || staff.staff_name || "Unknown Staff",
                name: staff.name || staff.staff_name || "Unknown Staff",
                designation:
                  staff.designation || staff.designation || staff.title || "",
                department:
                  staff.department ||
                  staff.department_name ||
                  staff.department ||
                  "",
                departmentID:
                  staff.departmentID ||
                  staff.department_id ||
                  staff.departmentID ||
                  "",
              };
            }
            return null;
          } catch (error) {
            console.error(`Error loading staff ${staffID}:`, error);
            return null;
          }
        });

        const staffMembers = await Promise.all(allStaffPromises);
        this.selectedStaffMembers = staffMembers.filter(
          (staff) => staff !== null
        );

        // Also populate staff options so the dropdown shows current selection
        this.staffOptions = [...this.selectedStaffMembers];
      } catch (error) {
        console.error("Error loading staff for edit:", error);
        this.selectedStaffMembers = [];
      } finally {
        this.staffLoading = false;
      }
    },

    // Load student data for editing when sIDs are available
    async loadStudentsForEdit(sIDsString) {
      if (!sIDsString) return;

      const studentIDs = sIDsString.split(",");
      this.studentLoading = true;

      try {
        // We'll need to search for each student
        // Since we don't have a direct API to get students by IDs, we'll use a workaround
        const allStudentsPromises = studentIDs.map(async (studentID) => {
          try {
            const obj = {
              url: `${this.$store.state.domain}Students/Search?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}&keyword=${studentID}&type=`,
              token: this.$store.state.userData.token,
            };
            const response = await this.get(obj);
            // Find the exact student by ID
            const student = response.find(
              (student) => student.id.toString() === studentID.toString()
            );

            if (student) {
              // Normalize the student data
              return {
                ...student,
                displayName:
                  student.name || student.student_name || "Unknown Student",
                name: student.name || student.student_name || "Unknown Student",
                father_name:
                  student.father_name ||
                  student.fatherName ||
                  student.father_name ||
                  "",
                mother_name:
                  student.mother_name ||
                  student.motherName ||
                  student.mother_name ||
                  "",
                roll_number:
                  student.roll_number ||
                  student.rollNumber ||
                  student.roll_number ||
                  "",
                className:
                  student.className ||
                  student.class_name ||
                  student.class ||
                  "",
                sectionName:
                  student.sectionName ||
                  student.section_name ||
                  student.section ||
                  "",
                admission_number:
                  student.admission_number ||
                  student.admissionNumber ||
                  student.admission_number ||
                  "",
                phone:
                  student.phone ||
                  student.phone_number ||
                  student.contact ||
                  "",
                address: student.address || student.full_address || "",
                gender: student.gender || student.sex || "",
                classID:
                  student.classID || student.class_id || student.classID || "",
                sectionID:
                  student.sectionID ||
                  student.section_id ||
                  student.sectionID ||
                  "",
                grNo: student.grNo || student.gr_no || student.gr_number || "", // Added grNo
              };
            }
            return null;
          } catch (error) {
            console.error(`Error loading student ${studentID}:`, error);
            return null;
          }
        });

        const students = await Promise.all(allStudentsPromises);
        this.selectedStudentMembers = students.filter(
          (student) => student !== null
        );

        // Also populate student options so the dropdown shows current selection
        this.studentOptions = [...this.selectedStudentMembers];
      } catch (error) {
        console.error("Error loading students for edit:", error);
        this.selectedStudentMembers = [];
      } finally {
        this.studentLoading = false;
      }
    },
  },
  computed: {
    sidebarSubtitle() {
      return this.myObj.id > 0
        ? "Update circular details"
        : "Create a new school circular";
    },
    displayedRange() {
      if (!this.totalCount) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    hasStudentClassSelected() {
      const v = this.myObj.classIDs;
      if (Array.isArray(v)) return v.length > 0;
      return v !== "" && v !== null && v !== undefined;
    },
    hasStudentSectionSelected() {
      const v = this.myObj.secIDs;
      if (Array.isArray(v)) return v.length > 0;
      return v !== "" && v !== null && v !== undefined;
    },
    hasDepartmentSelected() {
      const v = this.myObj.depID;
      if (Array.isArray(v)) return v.length > 0;
      return v !== "" && v !== null && v !== undefined;
    },
    filteredItems() {
      if (!this.searchQuery) return this.items;

      const search = this.searchQuery.toLowerCase();
      return this.items.filter((item) => {
        const titleMatch = item.title?.toLowerCase().includes(search);
        const descriptionMatch = item.description
          ?.toLowerCase()
          .includes(search);
        const recipientMatch = item.circularFor?.toLowerCase().includes(search);
        const metaDataMatch = item.metaData?.toLowerCase().includes(search);
        const recipientLabelMatch = this.getRecipientDisplay(item)
          .toLowerCase()
          .includes(search);
        return (
          titleMatch ||
          descriptionMatch ||
          recipientMatch ||
          metaDataMatch ||
          recipientLabelMatch
        );
      });
    },
    headOfficeCampusId() {
      return (
        (this.headOfficeObj &&
          (this.headOfficeObj.valueNumber ??
            this.headOfficeObj.value ??
            this.headOfficeObj.valueInt)) ||
        0
      );
    },
    headOfficeAvailable() {
      return (
        this.headOfficeObj &&
        this.headOfficeObj.valueNumber !== null &&
        this.headOfficeObj.valueNumber !== undefined &&
        this.headOfficeObj.valueNumber !== ""
      );
    },
    shouldShowCampusDropdown() {
      return this.headOfficeAvailable;
    },
    isAllCampusesSelected() {
      return (
        this.headOfficeAvailable &&
        this.selectedCampuses.includes(this.headOfficeCampusId)
      );
    },
    isMultipleCampusesSelected() {
      // Check if more than 1 campus is selected (excluding "All" if it's selected)
      const campusesWithoutAll = this.selectedCampuses.filter(
        (id) => id !== this.headOfficeCampusId
      );
      return campusesWithoutAll.length > 1 || this.isAllCampusesSelected;
    },
    allCampusIDs() {
      // Get selected campus IDs (excluding "All" if it's selected)
      if (this.selectedCampuses.length === 0) return "";

      // If "All" is selected, return all campus IDs except "All"
      if (this.isAllCampusesSelected) {
        return this.campuses
          .filter((c) => c.id !== this.headOfficeCampusId)
          .map((c) => c.id)
          .join(",");
      }

      // Otherwise, return the selected campus IDs
      return this.selectedCampuses.join(",");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";
@import "./circularsModern.scss";
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.filter-clear-btn {
  width: 46px;
  min-width: 46px;
  height: 46px;
  padding: 0;
  border-radius: 12px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.border-dashed {
  border: 2px dashed var(--primary);
}

.custom-toggle {
  .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #28a745;
    border-color: #28a745;
  }

  .custom-control-input:not(:checked) ~ .custom-control-label::before {
    background-color: #6c757d;
    border-color: #6c757d;
  }

  .custom-control-label::before {
    width: 2.5rem;
    height: 1.25rem;
    border-radius: 0.75rem;
  }

  .custom-control-label::after {
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    top: 0.125rem;
    left: 0.125rem;
  }

  .custom-control-input:checked ~ .custom-control-label::after {
    transform: translateX(1.25rem);
  }
}

.cursor-pointer {
  cursor: pointer;
}

.badge {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
}

// Enhanced Student Select Styles
.enhanced-student-select {
  .vs__dropdown-menu {
    max-height: 350px;
    overflow-y: auto;
  }

  // Fix selected/hover states
  .vs__dropdown-option {
    &.vs__dropdown-option--highlight {
      background: linear-gradient(135deg, #667eea, #764ba2) !important;
      color: #1a1a1a !important;

      .student-name,
      .staff-name {
        color: #1a1a1a !important;
        text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
        font-weight: 700;
      }

      .detail-item-compact {
        background: rgba(255, 255, 255, 0.9) !important;
        border-color: rgba(103, 126, 234, 0.3) !important;
        color: #2c3e50 !important;

        .detail-icon {
          color: #28a745 !important;
        }

        strong {
          color: #1a237e !important;
        }
      }

      .gr-number {
        background: rgba(255, 255, 255, 0.95) !important;
        border-color: #2196f3 !important;
        color: #1565c0 !important;

        .detail-icon {
          color: #1976d2 !important;
        }

        strong {
          color: #0d47a1 !important;
        }
      }

      .student-gender {
        background: rgba(255, 255, 255, 0.9) !important;
        border: 1px solid rgba(103, 126, 234, 0.3) !important;
        color: #2c3e50 !important;

        .detail-icon {
          color: #1976d2 !important;
        }
      }

      .staff-icon {
        background: rgba(255, 255, 255, 0.9) !important;
        border-color: rgba(103, 126, 234, 0.3) !important;

        .detail-icon {
          color: #667eea !important;
        }
      }
    }
  }
}

.student-option-item,
.staff-option-item {
  padding: 10px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  position: relative;

  &:hover {
    background: linear-gradient(135deg, #f8f9fa, #e9ecef);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    border-color: #28a745;
  }
}

.student-main-info,
.staff-main-info {
  width: 100%;
}

.student-name-section,
.staff-name-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.student-name,
.staff-name {
  color: #1a237e;
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
  transition: color 0.2s ease;
}

.student-gender {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &.male {
    background: linear-gradient(135deg, #1976d2, #42a5f5);
    color: white;
  }

  &.female {
    background: linear-gradient(135deg, #c2185b, #e91e63);
    color: white;
  }

  .detail-icon {
    color: white;
    transition: color 0.2s ease;
  }
}

.student-details-compact,
.staff-details-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.detail-item-compact {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #424242;
  font-size: 11px;
  padding: 3px 8px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  .detail-icon {
    color: #28a745;
    flex-shrink: 0;
    transition: color 0.2s ease;
  }

  strong {
    color: #2c3e50;
    font-weight: 600;
    transition: color 0.2s ease;
  }
}

.gr-number {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb) !important;
  border-color: #2196f3 !important;
  color: #1565c0 !important;

  .detail-icon {
    color: #1976d2 !important;
  }

  strong {
    color: #0d47a1 !important;
  }
}

.staff-designation {
  background: linear-gradient(135deg, #fff3e0, #ffe0b2) !important;
  border-color: #ff9800 !important;
  color: #e65100 !important;

  .detail-icon {
    color: #f57c00 !important;
  }

  strong {
    color: #bf360c !important;
  }
}

.staff-icon {
  display: inline-flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 8px;
  background: rgba(103, 126, 234, 0.1);
  border: 1px solid rgba(103, 126, 234, 0.2);

  .detail-icon {
    color: #667eea;
    transition: color 0.2s ease;
  }
}

// Selected Students Display
.selected-students-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-radius: 12px;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.22);

  .selected-count {
    font-size: 14px;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
}

.selected-students-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}

.selected-student-card {
  background: white;
  border: 1px solid #e8f5e8;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.4s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(40, 167, 69, 0.14);
    border-color: #28a745;
  }
}

.student-card-content {
  padding: 12px 14px;
}

.student-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
}

.student-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 12px;
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.25);
}

.student-card-info {
  flex: 1;
  min-width: 0;
}

.student-card-name {
  display: block;
  color: #1a237e;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.student-card-gr {
  display: block;
  color: #1976d2;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  padding: 3px 6px;
  border-radius: 4px;
  display: inline-block;
}

.remove-student-btn {
  background: none;
  border: none;
  color: #dc3545;
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;

  &:hover {
    background: linear-gradient(135deg, #ffebee, #ffcdd2);
    transform: scale(1.05);
    box-shadow: 0 3px 10px rgba(220, 53, 69, 0.25);
  }

  &:active {
    transform: scale(0.95);
  }
}

.student-card-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.detail-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  color: #495057;
  padding: 6px 10px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #e9ecef, #dee2e6);
    transform: translateY(-1px);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  }

  svg {
    color: #28a745;
    flex-shrink: 0;
  }
}

// Selected Staff Display
.selected-staff-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-radius: 12px;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(102, 126, 234, 0.22);

  .selected-count {
    font-size: 14px;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
}

.selected-staff-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}

.selected-staff-card {
  background: white;
  border: 1px solid #e8f5e8;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.4s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(40, 167, 69, 0.14);
    border-color: #28a745;
  }
}

.staff-card-content {
  padding: 12px 14px;
}

.staff-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
}

.staff-avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 12px;
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.25);
}

.staff-card-info {
  flex: 1;
  min-width: 0;
}

.staff-card-name {
  display: block;
  color: #1a237e;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.staff-card-designation {
  display: block;
  color: #1976d2;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  padding: 3px 6px;
  border-radius: 4px;
  display: inline-block;
}

.remove-staff-btn {
  background: none;
  border: none;
  color: #dc3545;
  padding: 4px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;

  &:hover {
    background: linear-gradient(135deg, #ffebee, #ffcdd2);
    transform: scale(1.05);
    box-shadow: 0 3px 10px rgba(220, 53, 69, 0.25);
  }

  &:active {
    transform: scale(0.95);
  }
}

.staff-card-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// Responsive adjustments
@media (max-width: 768px) {
  .selected-students-grid {
    grid-template-columns: 1fr;
  }

  .detail-row {
    flex-direction: column;
    gap: 6px;
  }

  .student-card-content {
    padding: 12px;
  }

  .student-avatar {
    width: 32px;
    height: 32px;
    margin-right: 8px;
  }

  .student-card-name {
    font-size: 14px;
  }

  .detail-chip {
    font-size: 11px;
    padding: 4px 8px;
  }
}

// Custom scrollbar for dropdown
.enhanced-student-select .vs__dropdown-menu::-webkit-scrollbar {
  width: 6px;
}

.enhanced-student-select .vs__dropdown-menu::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.enhanced-student-select .vs__dropdown-menu::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;

  &:hover {
    background: #a8a8a8;
  }
}

// Circular Details Modal Styles
.circular-details {
  padding: 10px 0;
}

.detail-item {
  background: #fafbfc;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
  height: 100%;

  &:hover {
    background: #f8f9fa;
    border-color: #d0d7de;
  }
}

.detail-label {
  display: flex;
  align-items: center;
  color: #5e5873;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;

  svg {
    color: #8b5cf6;
  }
}

.detail-value {
  color: #6e6b7b;
  font-size: 14px;
  line-height: 1.6;
  word-wrap: break-word;
}

.description-text {
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  padding: 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.attachments-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.attachment-btn {
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
}

.campuses-container {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e9ecef;
  max-height: 300px;
  overflow-y: auto;
}

.campuses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 10px;
}

.campus-chip {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  font-size: 13px;
  color: #5e5873;
  font-weight: 500;

  &:hover {
    background: linear-gradient(135deg, #f5f3ff, #ede9fe);
    border-color: #8b5cf6;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
  }

  svg {
    color: #8b5cf6;
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// Scrollbar styling for campuses container
.campuses-container::-webkit-scrollbar {
  width: 8px;
}

.campuses-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.campuses-container::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #8b5cf6 0%, #7c3aed 100%);
  border-radius: 4px;

  &:hover {
    background: linear-gradient(180deg, #7c3aed 0%, #6d28d9 100%);
  }
}

// Modal header styling
.modal-header {
  border-bottom: 2px solid #f0f0f0;
  padding: 1.25rem 1.5rem;
}

.modal-title {
  font-weight: 600;
  color: #5e5873;
  font-size: 1.25rem;
}

.btn-close {
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6e6b7b;
  transition: all 0.2s ease;

  &:hover {
    color: #5e5873;
    background: #f8f9fa;
    border-radius: 4px;
  }
}

// Responsive adjustments for campuses
@media (max-width: 768px) {
  .campuses-grid {
    grid-template-columns: 1fr;
  }

  .campuses-container {
    max-height: 250px;
  }
}
</style>
